import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { cover, rem } from 'polished';
import HelperText from '../helper-text';
import RatioImg from '../../ratio-img';

const placeholderPhotoStyles = `
  position: absolute;
  top: 50%;
  right: 50%;
  width: 50%;
  transform: translate(50%, -50%) rotate(6deg);
  filter: grayscale(100%);
  box-shadow: 9px 22px 39.69px 9.31px rgba(0, 0, 0, .09);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(255, 255, 255, .75);
    display: block;
    z-index: 1;
  }
`;

export default class FileUpload extends Component {

  static propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    buttonText: PropTypes.string,
    helperText: PropTypes.bool,
    errors: PropTypes.string,
  }

  static defaultProps = {
    helperText: true,
    buttonText: 'Upload Photo',
  }

  state = {
    imgSrc: undefined,
  }

  handleBlur = () => {
    // this is going to call setFieldTouched and manually update touched.topcis
    this.props.onBlur(this.props.name, true);
  };

  handleImages = (e) => {
    const [file] = e.target.files;

    if (!file || (file && file.size > 2000000)) {
      alert(`You have exceeded the 2mb filesize `); // eslint-disable-line

      return;
    }

    this.props.onChange(this.props.name, file);

    this.handleImageUrl(file)
      .then((img) => {
        this.setState({
          imgSrc: img,
        });
      });
  }

  // eslint-disable-next-line arrow-body-style
  handleImageUrl = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        resolve(reader.result);
      };

      reader.readAsDataURL(file);
    });
  }

  render() {
    const id = `file-${this.props.name}`;

    return (
      <FileUploadWrap>
        <SecondaryLabel>{this.props.label}</SecondaryLabel>
        <FileInputWrap>
          <RatioImg
            width={340}
            height={220}
            src="/static/images/file-upload.jpg"
            role="presentation"
            styles={placeholderPhotoStyles}
          />
          <FileInput
            name={this.props.name}
            id={id}
            type="file"
            onChange={this.handleImages}
            onBlur={this.handleBlur}
            required={this.props.required}
            accept="image/*"
            hasvalue={!!this.state.imgSrc}
          />
          <label htmlFor={id}>
            <span>{this.props.buttonText}</span>
          </label>

          {this.state.imgSrc &&
            <FileImg src={this.state.imgSrc} alt="profile upload" />
          }
        </FileInputWrap>

        <HelperText style={{ position: 'static', marginTop: '10px' }}>
          {this.props.helperText && !this.props.errors &&
            <HelperText.Text>
              Files must be less than 2mb. By uploading a photo you agree to
              the <a href="/photo-agreement" target="_blank" rel="noopener noreferrer">image uploading terms and agreements</a> and
              understand that all photos are subject to approval before being shown on this site.
            </HelperText.Text>
          }
          {this.props.errors &&
            <HelperText.Error>{this.props.errors}</HelperText.Error>
          }
        </HelperText>
      </FileUploadWrap>
    );
  }
}

const FileUploadWrap = styled.div`
  position: relative;
  padding-bottom: 1rem;
`;

const FileInputWrap = styled.div`
  position: relative;
  width: 100%;
  background-color: ${props => props.theme.lightGray};

  &::after {
    content: "";
    padding-bottom: 53%;
    width: 100%;
    display: block;
  }
`;

const SecondaryLabel = styled.p`
  margin-top: 0;
  font-size: 1rem;
`;

const FileInput = styled.input`
  width: .1px;
  height: .1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  color: inherit;

  + label {
    ${cover()}
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    text-align: center;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    overflow: hidden;

    span {
      background-color: ${props => props.hasvalue ? props.theme.blue : props.theme.black};
      font-size: 1.25rem;
      width: 276px;
      color: #fff;
      line-height: ${rem(66)};
      font-weight: 600;
      cursor: pointer;
      transition: background-color .4s ease-in, color .4s ease-in, opacity .2s ease-in;
      z-index: 1;
      opacity: ${props => props.hasvalue ? 0 : 1};
    }
  }

  &:focus + label,
  + label:hover {
    span {
      background-color: ${props => props.theme.blue};
      opacity: 1;
    }
  }
`;

const FileImg = styled.img`
  ${cover()}
  display: block;
  width: 100%;
  height: 100%;
  margin: auto;
  object-fit: scale-down;
  pointer-events: none;
`;
